import { Community } from '@/mocks/communities';
import GlowButton from '@/components/base/GlowButton';

interface DetailSidebarProps {
  community: Community;
}

const adImage = 'https://readdy.ai/api/search-image?query=minimalist%20frosted%20glass%20abstract%20soft%20pale%20blue%20white%20mist%20translucent%20vertical%20layered%20diamond%20geometric%20shapes%20subtle%20gradient%20clean%20light%20background%20serene%20elegant%20modern%20portrait%20panel&width=288&height=200&seq=ds201&orientation=portrait';

export default function DetailSidebar({ community }: DetailSidebarProps) {
  const formatCount = (n: number) => (n >= 1000 ? `${(n / 1000).toFixed(1)}k` : n.toString());

  return (
    <aside className="flex flex-col gap-4">
      {/* Quick Info Card */}
      <div className="bg-[#ddeaf5]/75 backdrop-blur-xl rounded-2xl border border-[#b8d4ea]/60 p-4">
        <h3 className="text-sm font-bold text-slate-700 mb-3 flex items-center gap-2">
          <span className="w-4 h-4 flex items-center justify-center text-[#0088CC]">
            <i className="ri-bar-chart-box-line text-sm"></i>
          </span>
          Community Stats
        </h3>
        <div className="flex flex-col gap-2.5">
          {[
            { icon: 'ri-group-line', label: 'Total Members', value: formatCount(community.memberCount) },
            { icon: 'ri-apps-line', label: 'Category', value: community.category },
            { icon: 'ri-global-line', label: 'Region', value: community.region },
            { icon: 'ri-translate-2', label: 'Language', value: community.language ?? 'English' },
            { icon: 'ri-message-3-line', label: 'Weekly Messages', value: community.weeklyMessages ? formatCount(community.weeklyMessages) : 'N/A' },
            { icon: 'ri-user-star-line', label: 'Admins', value: String(community.adminCount ?? 'N/A') },
          ].map((item) => (
            <div key={item.label} className="flex items-center justify-between py-2 border-b border-[#b8d4ea]/30 last:border-0">
              <div className="flex items-center gap-2 text-slate-500">
                <span className="w-4 h-4 flex items-center justify-center text-[#0088CC]">
                  <i className={`${item.icon} text-xs`}></i>
                </span>
                <span className="text-xs">{item.label}</span>
              </div>
              <span className="text-xs font-semibold text-slate-700">{item.value}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Join CTA */}
      <div className="bg-gradient-to-br from-[#0088CC]/10 to-[#0066AA]/5 border border-[#0088CC]/20 backdrop-blur-md rounded-2xl p-5 text-center">
        <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#0088CC]/15 mx-auto mb-3">
          <i className="ri-telegram-fill text-[#0088CC] text-2xl"></i>
        </div>
        <h3 className="text-sm font-bold text-slate-700 mb-1">Ready to Join?</h3>
        <p className="text-xs text-slate-400 mb-4 leading-relaxed">
          Connect with {formatCount(community.memberCount)} members in this community right now.
        </p>
        <GlowButton
          as="a"
          href={community.telegramLink}
          target="_blank"
          rel="nofollow noopener noreferrer"
          className="w-full text-sm font-bold py-3 rounded-xl"
        >
          <i className="ri-telegram-fill text-base"></i>
          Open in Telegram
        </GlowButton>
      </div>

      {/* Advertisement */}
      <div className="rounded-2xl border border-dashed border-[#0088CC]/25 bg-white/60 backdrop-blur-md overflow-hidden">
        <div className="px-3 py-1.5 border-b border-slate-100">
          <span className="text-[10px] font-semibold text-[#0088CC] uppercase tracking-wider">Advertisement</span>
        </div>
        <div className="w-full h-44 overflow-hidden">
          <img
            src={adImage}
            alt="Advertisement"
            className="w-full h-full object-cover object-top"
          />
        </div>
        <div className="p-3">
          <p className="text-xs font-semibold text-slate-700 mb-0.5">Telegram Premium</p>
          <p className="text-[11px] text-slate-400 mb-2">Unlock exclusive features — faster downloads, unique stickers, and more.</p>
          <a href="https://t.me/liketgli" target="_blank" rel="nofollow noopener noreferrer" className="text-xs text-[#0088CC] hover:underline cursor-pointer font-medium">Advertise here →</a>
        </div>
      </div>

      {/* Submit CTA */}
      <div id="submit" className="rounded-2xl bg-white/55 backdrop-blur-md border border-[#b8d4ea]/50 p-5 text-center">
        <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#0088CC]/10 mx-auto mb-3">
          <i className="ri-add-circle-line text-[#0088CC] text-xl"></i>
        </div>
        <h3 className="text-sm font-bold text-slate-700 mb-1">Own a Community?</h3>
        <p className="text-xs text-slate-400 mb-3 leading-relaxed">Get your Telegram community listed and reach thousands of users.</p>
        <GlowButton className="w-full text-sm font-semibold py-2.5 rounded-xl">
          Submit Community
        </GlowButton>
      </div>
    </aside>
  );
}
